/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { createRef, Component } from 'react';
import I18n from 'I18n';
import guessCountryFromDialCode from 'I18n/utils/guessCountryFromDialCode';
import formatPhoneNumber from 'I18n/utils/formatPhoneNumber';
import classNames from 'classnames';
import UITextInput from 'UIComponents/input/UITextInput';
import UISelect from 'UIComponents/input/UISelect';
import UIIcon from 'UIComponents/icon/UIIcon';
import CountryFlag from '../internal/components/CountryFlag';
import getPhoneOptions from '../internal/utils/getPhoneOptions';
import SupportedPhoneNumbers from '../constants/SupportedPhoneNumbers';
import * as TelephoneData from 'I18n/constants/TelephoneData';
const allCountries = TelephoneData.allCountries;
const iso2Lookup = TelephoneData.iso2Lookup;
const INVALID_OPTION = {
  dialCode: '',
  priority: 10001,
  iso2: null
};

const _mapPropsToState = (props, initial = false) => {
  let inputNumber;

  if (props.value) {
    inputNumber = props.value;
  } else {
    inputNumber = '';
  }

  let selectedCountryGuess = INVALID_OPTION;
  let formattedNumber = inputNumber;

  if (inputNumber === '' || inputNumber.indexOf('+') === 0) {
    selectedCountryGuess = guessCountryFromDialCode(inputNumber.replace(/\D/g, ''));
    const numberToFormat = inputNumber ? inputNumber.replace(/\D/g, '') : selectedCountryGuess.dialCode;
    formattedNumber = formatPhoneNumber(numberToFormat, selectedCountryGuess.format);
  }

  const state = {
    selectedCountry: selectedCountryGuess,
    formattedNumber
  };

  if (initial) {
    state.propsMemo = props.value;
  }

  return state;
};

export default class PhoneNumberInput extends Component {
  constructor(props, context) {
    super(props, context);

    this._cursorToEnd = () => {
      const input = this.numberInputRef.current;

      if (input) {
        input.focus();
      }
    };

    this.formatSimpleNumber = (number, hasSelectedValidCountry = true) => {
      const digitsOnly = number.replace(/\D/g, '');

      if (!hasSelectedValidCountry) {
        return digitsOnly;
      }

      return `+${digitsOnly}`;
    };

    this.handlePhoneNumberInput = event => {
      const {
        formattedNumber,
        selectedCountry
      } = this.state;
      const extension = this.props.extensionValue;
      const eventTarget = event.target;

      if (eventTarget.value === formattedNumber) {
        return;
      }

      let inputNumber = eventTarget.value.replace(/\D/g, '');

      if (inputNumber.indexOf(selectedCountry.dialCode) !== 0) {
        inputNumber = selectedCountry.dialCode;
      }

      if (this.props.onChange) {
        this.props.onChange({
          phoneNumber: this.formatSimpleNumber(inputNumber, selectedCountry.dialCode),
          extension,
          country: selectedCountry.iso2
        });
      }

      const selectionStart = eventTarget.selectionStart;
      const formattedInputNumber = formatPhoneNumber(inputNumber, selectedCountry.format);
      this.setState({
        formattedNumber: formattedInputNumber
      }, () => {
        let newSelectionStart = selectionStart;

        if (inputNumber === selectedCountry.dialCode || selectionStart === formattedNumber.length + 1) {
          newSelectionStart = formattedInputNumber.length + 1;
        }

        eventTarget.selectionStart = newSelectionStart;
        eventTarget.selectionEnd = newSelectionStart;
      });
    };

    this.handleExtensionInput = e => {
      const {
        selectedCountry,
        formattedNumber
      } = this.state;
      this.props.onChange({
        phoneNumber: this.formatSimpleNumber(formattedNumber, selectedCountry.dialCode),
        extension: e.target.value.replace(/\D/g, ''),
        country: selectedCountry.iso2
      });
    };

    this.handleFlagItemClick = e => {
      const extension = this.props.extensionValue;
      const {
        selectedCountry,
        formattedNumber
      } = this.state;
      const nextSelectedCountry = allCountries[iso2Lookup[e.target.value]];

      if (selectedCountry.iso2 !== nextSelectedCountry.iso2) {
        const newNumber = selectedCountry.dialCode ? formattedNumber.replace(/\D/g, '').replace(selectedCountry.dialCode, nextSelectedCountry.dialCode) : `${nextSelectedCountry.dialCode}${formattedNumber}`;
        const newFormattedNumber = formatPhoneNumber(newNumber, nextSelectedCountry.format);
        this.setState({
          selectedCountry: nextSelectedCountry,
          formattedNumber: newFormattedNumber
        }, () => {
          this._cursorToEnd();

          if (this.props.onChange) {
            this.props.onChange({
              phoneNumber: this.formatSimpleNumber(newNumber, selectedCountry.dialCode || nextSelectedCountry.dialCode),
              extension,
              country: nextSelectedCountry.iso2
            });
          }
        });
      }
    };

    this.getCountryDropDownList = (hasSelectedValidCountry, validCountries, supportedCountrySet) => {
      let countryDropDownList = getPhoneOptions(allCountries);

      const checkSupportedCountries = country => SupportedPhoneNumbers[supportedCountrySet].indexOf(country.value) >= 0;

      const checkValidCountries = country => validCountries.indexOf(country.value) >= 0;

      if (supportedCountrySet && validCountries) {
        countryDropDownList = countryDropDownList.filter(country => checkSupportedCountries(country) && checkValidCountries(country));
      } else if (supportedCountrySet) {
        countryDropDownList = countryDropDownList.filter(checkSupportedCountries);
      } else if (validCountries) {
        countryDropDownList = countryDropDownList.filter(checkValidCountries);
      }

      if (!hasSelectedValidCountry) {
        return [{
          value: INVALID_OPTION.iso2,
          text: I18n.text('i18nAddon.countryInfo.invalidCountryOption')
        }, ...countryDropDownList];
      }

      return countryDropDownList;
    };

    this.buildValueRenderer = countryIso => {
      return () => {
        if (!countryIso) {
          return /*#__PURE__*/_jsx(UIIcon, {
            name: "warning"
          });
        }

        return /*#__PURE__*/_jsx(CountryFlag, {
          countryCode: countryIso
        });
      };
    };

    this.renderExtension = (extensionValue, extensionInputProps) => {
      if (typeof extensionValue !== 'string') {
        return null;
      }

      return /*#__PURE__*/_jsx(UITextInput, Object.assign({}, extensionInputProps, {
        className: classNames('extension-input', extensionInputProps.className),
        placeholder: I18n.text('i18nAddon.phoneNumberInput.extensionPlaceholder'),
        onChange: this.handleExtensionInput,
        value: extensionValue
      }));
    };

    this.state = _mapPropsToState(props, true);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.value === null || nextProps.value === undefined) {
      return Object.assign({}, prevState, {}, _mapPropsToState(nextProps));
    } else {
      const nextValueDigits = nextProps.value.replace(/\D/g, '');

      if (prevState.propsMemo !== nextProps.value) {
        const formatted = formatPhoneNumber(nextValueDigits, prevState.selectedCountry.format);
        return Object.assign({}, prevState, {
          formattedNumber: formatted,
          propsMemo: nextProps.value
        });
      }
    }

    return null;
  }

  render() {
    const {
      selectedCountry,
      formattedNumber
    } = this.state;

    const _this$props = this.props,
          {
      className,
      countrySelectProps,
      extensionInputProps,
      extensionValue,
      phoneInputProps,
      validCountries,
      supportedCountrySet
    } = _this$props,
          passThroughProps = _objectWithoutPropertiesLoose(_this$props, ["className", "countrySelectProps", "extensionInputProps", "extensionValue", "phoneInputProps", "onChange", "validCountries", "supportedCountrySet"]);

    this.numberInputRef = this.numberInputRef || /*#__PURE__*/createRef();
    return /*#__PURE__*/_jsxs("div", Object.assign({}, passThroughProps, {
      className: classNames('phone-number-input', className),
      children: [/*#__PURE__*/_jsx(UISelect, Object.assign({}, countrySelectProps, {
        className: classNames('country-select', countrySelectProps.className),
        dropdownClassName: classNames('country-dropdown', countrySelectProps.dropdownClassName),
        onChange: this.handleFlagItemClick,
        options: this.getCountryDropDownList(selectedCountry.dialCode, validCountries, supportedCountrySet),
        value: selectedCountry.iso2,
        valueRenderer: this.buildValueRenderer(selectedCountry.iso2)
      })), /*#__PURE__*/_jsx(UITextInput, Object.assign({}, phoneInputProps, {
        className: classNames('phone-text-input', phoneInputProps.className),
        inputRef: this.numberInputRef,
        onChange: this.handlePhoneNumberInput,
        value: formattedNumber,
        type: "tel"
      })), this.renderExtension(extensionValue, extensionInputProps)]
    }));
  }

}
PhoneNumberInput.propTypes = {
  countrySelectProps: PropTypes.object,
  extensionInputProps: PropTypes.object,
  extensionValue: PropTypes.string,
  onChange: PropTypes.func,
  phoneInputProps: PropTypes.object,
  supportedCountrySet: PropTypes.oneOf(Object.keys(SupportedPhoneNumbers)),
  validCountries: PropTypes.array,
  value: PropTypes.string
};
PhoneNumberInput.defaultProps = {
  countrySelectProps: {},
  extensionInputProps: {},
  phoneInputProps: {}
};